/**
 * 🚀 ИНСТРУМЕНТ ИЗМЕРЕНИЯ ПРОИЗВОДИТЕЛЬНОСТИ
 * - Реальные метрики Web Vitals
 * - Анализ времени загрузки ресурсов
 * - Мониторинг использования памяти
 * - Детекция узких мест
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      navigation: {},
      resources: [],
      vitals: {},
      memory: {},
      custom: {}
    };
    this.observers = [];
    this.isMonitoring = false;
  }

  /**
   * Инициализация мониторинга
   */
  initialize() {
    if (this.isMonitoring) return;
    
    console.log('🚀 Performance Monitor started');
    
    this.isMonitoring = true;
    this.setupNavigationMetrics();
    this.setupResourceMetrics();
    this.setupWebVitals();
    this.setupMemoryMonitoring();
    this.setupCustomMetrics();
  }

  /**
   * Метрики навигации
   */
  setupNavigationMetrics() {
    if (!window.performance || !window.performance.navigation) return;

    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        
        this.metrics.navigation = {
          // Время до первого байта
          ttfb: navigation.responseStart - navigation.requestStart,
          
          // Время загрузки DOM
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.navigationStart,
          
          // Полное время загрузки
          loadComplete: navigation.loadEventEnd - navigation.navigationStart,
          
          // DNS lookup время
          dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
          
          // Время установления соединения
          connectionTime: navigation.connectEnd - navigation.connectStart,
          
          // Время ответа сервера
          serverResponse: navigation.responseEnd - navigation.responseStart
        };

        console.log('📊 Navigation Metrics:', this.metrics.navigation);
      }, 0);
    });
  }

  /**
   * Метрики ресурсов
   */
  setupResourceMetrics() {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceMetric = {
            name: entry.name,
            type: this.getResourceType(entry.name),
            size: entry.transferSize || entry.encodedBodySize,
            duration: entry.duration,
            startTime: entry.startTime,
            cached: entry.transferSize === 0 && entry.encodedBodySize > 0
          };
          
          this.metrics.resources.push(resourceMetric);
          
          // Логируем медленные ресурсы
          if (entry.duration > 1000) {
            console.warn('🐌 Slow resource:', resourceMetric);
          }
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });
    this.observers.push(observer);
  }

  /**
   * Web Vitals метрики
   */
  setupWebVitals() {
    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.vitals.lcp = lastEntry.startTime;
      console.log('📊 LCP:', lastEntry.startTime, 'ms');
    });
    
    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(lcpObserver);
    } catch (e) {
      console.warn('LCP not supported');
    }

    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        this.metrics.vitals.fid = entry.processingStart - entry.startTime;
        console.log('📊 FID:', this.metrics.vitals.fid, 'ms');
      }
    });
    
    try {
      fidObserver.observe({ type: 'first-input', buffered: true });
      this.observers.push(fidObserver);
    } catch (e) {
      console.warn('FID not supported');
    }

    // Cumulative Layout Shift
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
      this.metrics.vitals.cls = clsValue;
      console.log('📊 CLS:', clsValue);
    });
    
    try {
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(clsObserver);
    } catch (e) {
      console.warn('CLS not supported');
    }
  }

  /**
   * Мониторинг памяти
   */
  setupMemoryMonitoring() {
    if (!window.performance.memory) return;

    const checkMemory = () => {
      this.metrics.memory = {
        used: Math.round(window.performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(window.performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(window.performance.memory.jsHeapSizeLimit / 1048576) // MB
      };
    };

    checkMemory();
    setInterval(checkMemory, 5000); // Каждые 5 секунд
  }

  /**
   * Пользовательские метрики
   */
  setupCustomMetrics() {
    // Время до интерактивности React
    this.markTime('react-start');
    
    // Время загрузки изображений
    this.trackImageLoading();
    
    // Время API запросов
    this.trackAPIRequests();
  }

  /**
   * Отслеживание загрузки изображений
   */
  trackImageLoading() {
    const images = document.querySelectorAll('img');
    let loadedImages = 0;
    const totalImages = images.length;

    if (totalImages === 0) return;

    const startTime = performance.now();

    images.forEach(img => {
      const checkLoaded = () => {
        loadedImages++;
        if (loadedImages === totalImages) {
          const loadTime = performance.now() - startTime;
          this.metrics.custom.allImagesLoaded = loadTime;
          console.log('📊 All images loaded in:', loadTime, 'ms');
        }
      };

      if (img.complete) {
        checkLoaded();
      } else {
        img.addEventListener('load', checkLoaded);
        img.addEventListener('error', checkLoaded);
      }
    });
  }

  /**
   * Отслеживание API запросов
   */
  trackAPIRequests() {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = args[0];
      
      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - startTime;
        
        if (url.includes('/api/')) {
          console.log(`📊 API Request: ${url} - ${duration.toFixed(2)}ms`);
          
          if (!this.metrics.custom.apiRequests) {
            this.metrics.custom.apiRequests = [];
          }
          
          this.metrics.custom.apiRequests.push({
            url,
            duration,
            status: response.status,
            cached: response.headers.get('x-cache') === 'HIT'
          });
        }
        
        return response;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.warn(`📊 API Error: ${url} - ${duration.toFixed(2)}ms`, error);
        throw error;
      }
    };
  }

  /**
   * Маркировка времени
   */
  markTime(name) {
    performance.mark(name);
  }

  /**
   * Измерение времени между метками
   */
  measureTime(name, startMark, endMark = null) {
    if (endMark) {
      performance.measure(name, startMark, endMark);
    } else {
      performance.measure(name, startMark);
    }
    
    const measure = performance.getEntriesByName(name, 'measure')[0];
    console.log(`📊 ${name}:`, measure.duration, 'ms');
    return measure.duration;
  }

  /**
   * Определение типа ресурса
   */
  getResourceType(url) {
    if (url.includes('.css')) return 'css';
    if (url.includes('.js')) return 'js';
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|avif)$/i)) return 'image';
    if (url.includes('/api/')) return 'api';
    if (url.includes('.woff')) return 'font';
    return 'other';
  }

  /**
   * Получение отчета о производительности
   */
  getPerformanceReport() {
    const report = {
      ...this.metrics,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Анализ и рекомендации
    const recommendations = this.generateRecommendations();
    report.recommendations = recommendations;

    return report;
  }

  /**
   * Генерация рекомендаций
   */
  generateRecommendations() {
    const recommendations = [];
    
    // Анализ Web Vitals
    if (this.metrics.vitals.lcp > 2500) {
      recommendations.push({
        type: 'performance',
        severity: 'high',
        message: 'LCP превышает 2.5s. Оптимизируйте загрузку изображений и критического CSS.'
      });
    }
    
    if (this.metrics.vitals.fid > 100) {
      recommendations.push({
        type: 'performance',
        severity: 'medium',
        message: 'FID превышает 100ms. Оптимизируйте JavaScript выполнение.'
      });
    }
    
    if (this.metrics.vitals.cls > 0.1) {
      recommendations.push({
        type: 'performance',
        severity: 'medium',
        message: 'CLS превышает 0.1. Добавьте размеры для изображений и зарезервируйте место для динамического контента.'
      });
    }

    // Анализ ресурсов
    const largeImages = this.metrics.resources.filter(r => 
      r.type === 'image' && r.size > 500000 // > 500KB
    );
    
    if (largeImages.length > 0) {
      recommendations.push({
        type: 'optimization',
        severity: 'medium',
        message: `Найдено ${largeImages.length} изображений больше 500KB. Рассмотрите сжатие.`
      });
    }

    // Анализ памяти
    if (this.metrics.memory.used > 100) {
      recommendations.push({
        type: 'memory',
        severity: 'low',
        message: `Использование памяти: ${this.metrics.memory.used}MB. Мониторьте утечки памяти.`
      });
    }

    return recommendations;
  }

  /**
   * Вывод отчета в консоль
   */
  logReport() {
    const report = this.getPerformanceReport();
    
    console.group('🚀 ОТЧЕТ О ПРОИЗВОДИТЕЛЬНОСТИ');
    console.log('📊 Навигация:', report.navigation);
    console.log('📊 Web Vitals:', report.vitals);
    console.log('📊 Память:', report.memory);
    console.log('📊 Пользовательские метрики:', report.custom);
    
    if (report.recommendations.length > 0) {
      console.group('💡 Рекомендации');
      report.recommendations.forEach(rec => {
        const icon = rec.severity === 'high' ? '🔴' : rec.severity === 'medium' ? '🟡' : '🟢';
        console.log(`${icon} ${rec.message}`);
      });
      console.groupEnd();
    }
    
    console.groupEnd();
    
    return report;
  }

  /**
   * Очистка observers
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.isMonitoring = false;
  }
}

// Создаем глобальный экземпляр
window.performanceMonitor = new PerformanceMonitor();

// Автозапуск в development режиме
if (process.env.NODE_ENV === 'development') {
  window.performanceMonitor.initialize();
  
  // Логи отчета через 5 секунд после загрузки
  window.addEventListener('load', () => {
    setTimeout(() => {
      window.performanceMonitor.logReport();
    }, 5000);
  });
}

export default window.performanceMonitor;